// src/utils/collegeData.js
/*
This file serves as the master data source for the top colleges featured in the app.
Manually curating this data ensures a high-quality, consistent user experience for the most
viewed profile pages. The AI/RAG system will be used for personalized analytics on top of this data,
not for generating the core display information.
*/

export const collegeData = [
  {
    name: 'Princeton University',
    location: 'Princeton, NJ',
    type: 'Private',
    stats: {
      acceptanceRate: '4.4',
      gpa: { p25: '3.90', p75: '4.00+' },
      sat: { p25: 1460, p75: 1570 },
      act: { p25: 33, p75: 35 },
      yieldRate: '69',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '5:1',
      fourYearGraduationRate: '89',
      studentCount: '5,321',
      cost: {
        tuitionAndFees: '$57,410',
        roomAndBoard: '$18,800',
        inStateTuition: '$57,410',
      },
      demographics: {
        maleFemaleRatio: '50% / 50%',
        internationalStudents: '13.0',
        raceEthnicity: {
          'Asian': 25.0,
          'Hispanic': 10.0,
          'White': 40.0,
          'Black': 8.0,
          'Two or more races': 7.0,
          'Nonresident Alien': 13.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'Massachusetts Institute of Technology',
    location: 'Cambridge, MA',
    type: 'Private',
    stats: {
      acceptanceRate: '4.7',
      gpa: { p25: '3.95', p75: '4.00+' },
      sat: { p25: 1520, p75: 1570 },
      act: { p25: 35, p75: 36 },
      yieldRate: '77',
      applicationDeadline: 'Jan 5',
      studentFacultyRatio: '3:1',
      fourYearGraduationRate: '96',
      studentCount: '4,530',
      cost: {
        tuitionAndFees: '$59,750',
        roomAndBoard: '$19,100',
        inStateTuition: '$59,750',
      },
      demographics: {
        maleFemaleRatio: '51% / 49%',
        internationalStudents: '11.0',
        raceEthnicity: {
          'Asian': 30.0,
          'Hispanic': 15.0,
          'White': 34.0,
          'Black': 6.0,
          'Two or more races': 8.0,
          'Nonresident Alien': 11.0,
          'Unknown': 1.0,
        },
      },
    },
  },
  {
    name: 'Harvard University',
    location: 'Cambridge, MA',
    type: 'Private',
    stats: {
      acceptanceRate: '3.6',
      yieldRate: '83',
      applicationDeadline: 'Jan 1',
      satRange: '1460-1580',
      actRange: '33-35',
      gpa: { p25: '3.90', p75: '4.00+' },
      sat: { p25: 1460, p75: 1580 },
      act: { p25: 33, p75: 35 },
      studentFacultyRatio: '7:1',
      fourYearGraduationRate: '86',
      studentCount: '7,153',
      cost: {
        tuitionAndFees: '$59,076',
        roomAndBoard: '$21,180',
        inStateTuition: '$59,076',
      },
      demographics: {
        maleFemaleRatio: '49% / 51%',
        internationalStudents: '12.8',
        raceEthnicity: {
          'Asian': 20.6,
          'Hispanic': 11.8,
          'White': 37.8,
          'Black': 7.9,
          'Two or more races': 10.3,
          'Nonresident Alien': 11.4,
          'Unknown': 0.2,
        },
      },
    },
  },
  {
    name: 'Stanford University',
    location: 'Stanford, CA',
    type: 'Private',
    stats: {
      acceptanceRate: '3.9',
      gpa: { p25: '3.90', p75: '4.20' },
      sat: { p25: 1470, p75: 1570 },
      act: { p25: 32, p75: 35 },
      yieldRate: '82',
      applicationDeadline: 'Jan 5',
      studentFacultyRatio: '5:1',
      fourYearGraduationRate: '85',
      studentCount: '8,038',
      cost: {
        tuitionAndFees: '$62,448',
        roomAndBoard: '$20,178',
        inStateTuition: '$62,448',
      },
      demographics: {
        maleFemaleRatio: '50% / 50%',
        internationalStudents: '11.0',
        raceEthnicity: {
          'Asian': 25.6,
          'Hispanic': 17.4,
          'White': 32.5,
          'Black': 6.8,
          'Two or more races': 10.5,
          'Nonresident Alien': 7.0,
          'Unknown': 0.2,
        },
      },
    },
  },
  {
    name: 'Yale University',
    location: 'New Haven, CT',
    type: 'Private',
    stats: {
      acceptanceRate: '4.6',
      gpa: { p25: '3.92', p75: '4.00+' },
      sat: { p25: 1460, p75: 1580 },
      act: { p25: 33, p75: 35 },
      yieldRate: '70',
      applicationDeadline: 'Jan 2',
      studentFacultyRatio: '6:1',
      fourYearGraduationRate: '88',
      studentCount: '6,536',
      cost: {
        tuitionAndFees: '$64,700',
        roomAndBoard: '$19,800',
        inStateTuition: '$64,700',
      },
      demographics: {
        maleFemaleRatio: '49% / 51%',
        internationalStudents: '12.0',
        raceEthnicity: {
          'Asian': 27.0,
          'Hispanic': 14.0,
          'White': 35.0,
          'Black': 8.0,
          'Two or more races': 6.0,
          'Nonresident Alien': 12.0,
          'Unknown': 1.0,
        },
      },
    },
  },
  {
    name: 'California Institute of Technology',
    location: 'Pasadena, CA',
    type: 'Private',
    stats: {
      acceptanceRate: '2.7',
      gpa: { p25: '3.95', p75: '4.30' },
      sat: { p25: 1530, p75: 1570 },
      act: { p25: 35, p75: 36 },
      yieldRate: '42',
      applicationDeadline: 'Jan 3',
      studentFacultyRatio: '3:1',
      fourYearGraduationRate: '89',
      studentCount: '987',
      cost: {
        tuitionAndFees: '$60,864',
        roomAndBoard: '$19,800',
        inStateTuition: '$60,864',
      },
      demographics: {
        maleFemaleRatio: '55% / 45%',
        internationalStudents: '10.0',
        raceEthnicity: {
          'Asian': 44.0,
          'Hispanic': 13.0,
          'White': 25.0,
          'Black': 1.0,
          'Two or more races': 7.0,
          'Nonresident Alien': 10.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'Duke University',
    location: 'Durham, NC',
    type: 'Private',
    stats: {
      acceptanceRate: '6.3',
      gpa: { p25: '3.92', p75: '4.00+' },
      sat: { p25: 1470, p75: 1570 },
      act: { p25: 33, p75: 35 },
      yieldRate: '52',
      applicationDeadline: 'Jan 2',
      studentFacultyRatio: '6:1',
      fourYearGraduationRate: '90',
      studentCount: '6,717',
      cost: {
        tuitionAndFees: '$66,172',
        roomAndBoard: '$18,510',
        inStateTuition: '$66,172',
      },
      demographics: {
        maleFemaleRatio: '48% / 52%',
        internationalStudents: '13.0',
        raceEthnicity: {
          'Asian': 21.0,
          'Hispanic': 11.0,
          'White': 45.0,
          'Black': 10.0,
          'Two or more races': 6.0,
          'Nonresident Alien': 13.0,
          'Unknown': 4.0,
        },
      },
    },
  },
  {
    name: 'Johns Hopkins University',
    location: 'Baltimore, MD',
    type: 'Private',
    stats: {
      acceptanceRate: '6.3',
      gpa: { p25: '3.90', p75: '4.00+' },
      sat: { p25: 1460, p75: 1570 },
      act: { p25: 33, p75: 35 },
      yieldRate: '44',
      applicationDeadline: 'Jan 2',
      studentFacultyRatio: '7:1',
      fourYearGraduationRate: '89',
      studentCount: '6,063',
      cost: {
        tuitionAndFees: '$61,210',
        roomAndBoard: '$18,350',
        inStateTuition: '$61,210',
      },
      demographics: {
        maleFemaleRatio: '47% / 53%',
        internationalStudents: '12.0',
        raceEthnicity: {
          'Asian': 28.0,
          'Hispanic': 13.0,
          'White': 35.0,
          'Black': 7.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 12.0,
          'Unknown': 3.0,
        },
      },
    },
  },
  {
    name: 'Northwestern University',
    location: 'Evanston, IL',
    type: 'Private',
    stats: {
      acceptanceRate: '7.2',
      gpa: { p25: '3.90', p75: '4.00+' },
      sat: { p25: 1440, p75: 1550 },
      act: { p25: 32, p75: 35 },
      yieldRate: '46',
      applicationDeadline: 'Jan 3',
      studentFacultyRatio: '6:1',
      fourYearGraduationRate: '86',
      studentCount: '8,327',
      cost: {
        tuitionAndFees: '$63,468',
        roomAndBoard: '$19,692',
        inStateTuition: '$63,468',
      },
      demographics: {
        maleFemaleRatio: '47% / 53%',
        internationalStudents: '16.0',
        raceEthnicity: {
          'Asian': 19.0,
          'Hispanic': 13.0,
          'White': 42.0,
          'Black': 6.0,
          'Two or more races': 6.0,
          'Nonresident Alien': 16.0,
          'Unknown': 3.0,
        },
      },
    },
  },
  {
    name: 'University of Pennsylvania',
    location: 'Philadelphia, PA',
    type: 'Private',
    stats: {
      acceptanceRate: '6.5',
      gpa: { p25: '3.90', p75: '4.00+' },
      sat: { p25: 1470, p75: 1570 },
      act: { p25: 33, p75: 35 },
      yieldRate: '68',
      applicationDeadline: 'Jan 5',
      studentFacultyRatio: '6:1',
      fourYearGraduationRate: '86',
      studentCount: '10,019',
      cost: {
        tuitionAndFees: '$63,452',
        roomAndBoard: '$18,820',
        inStateTuition: '$63,452',
      },
      demographics: {
        maleFemaleRatio: '49% / 51%',
        internationalStudents: '13.0',
        raceEthnicity: {
          'Asian': 22.0,
          'Hispanic': 12.0,
          'White': 40.0,
          'Black': 7.0,
          'Two or more races': 6.0,
          'Nonresident Alien': 13.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'Cornell University',
    location: 'Ithaca, NY',
    type: 'Private',
    stats: {
      acceptanceRate: '8.7',
      gpa: { p25: '3.78', p75: '3.98' },
      sat: { p25: 1400, p75: 1540 },
      act: { p25: 32, p75: 35 },
      yieldRate: '65',
      applicationDeadline: 'Jan 2',
      studentFacultyRatio: '9:1',
      fourYearGraduationRate: '87',
      studentCount: '15,503',
      cost: {
        tuitionAndFees: '$65,204',
        roomAndBoard: '$19,800',
        inStateTuition: '$65,204',
      },
      demographics: {
        maleFemaleRatio: '49% / 51%',
        internationalStudents: '13.0',
        raceEthnicity: {
          'Asian': 25.0,
          'Hispanic': 12.0,
          'White': 35.0,
          'Black': 7.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 13.0,
          'Unknown': 3.0,
        },
      },
    },
  },
  {
    name: 'University of Chicago',
    location: 'Chicago, IL',
    type: 'Private',
    stats: {
      acceptanceRate: '5.4',
      gpa: { p25: '3.85', p75: '4.15' },
      sat: { p25: 1450, p75: 1560 },
      act: { p25: 33, p75: 35 },
      yieldRate: '75',
      applicationDeadline: 'Jan 3',
      studentFacultyRatio: '5:1',
      fourYearGraduationRate: '89',
      studentCount: '7,559',
      cost: {
        tuitionAndFees: '$64,446',
        roomAndBoard: '$19,800',
        inStateTuition: '$64,446',
      },
      demographics: {
        maleFemaleRatio: '50% / 50%',
        internationalStudents: '16.0',
        raceEthnicity: {
          'Asian': 30.0,
          'Hispanic': 12.0,
          'White': 30.0,
          'Black': 6.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 16.0,
          'Unknown': 1.0,
        },
      },
    },
  },
  {
    name: 'Brown University',
    location: 'Providence, RI',
    stats: {
      acceptanceRate: '5.0',
      gpa: { p25: '3.80', p75: '3.98' },
      sat: { p25: 1440, p75: 1560 },
      act: { p25: 32, p75: 35 },
      yieldRate: '62',
      applicationDeadline: 'Jan 3',
      studentFacultyRatio: '6:1',
      fourYearGraduationRate: '87',
      studentCount: '7,349',
      cost: {
        tuitionAndFees: '$65,656',
        roomAndBoard: '$19,800',
        inStateTuition: '$65,656',
      },
      demographics: {
        maleFemaleRatio: '45% / 55%',
        internationalStudents: '18.0',
        raceEthnicity: {
          'Asian': 25.0,
          'Hispanic': 12.0,
          'White': 35.0,
          'Black': 7.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 18.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'Columbia University',
    location: 'New York, NY',
    type: 'Private',
    stats: {
      acceptanceRate: '3.8',
      gpa: { p25: '3.95', p75: '4.00+' },
      sat: { p25: 1490, p75: 1570 },
      act: { p25: 34, p75: 35 },
      yieldRate: '63',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '6:1',
      fourYearGraduationRate: '95',
      studentCount: '8,902',
      cost: {
        tuitionAndFees: '$65,524',
        roomAndBoard: '$16,800',
        inStateTuition: '$65,524',
      },
      demographics: {
        maleFemaleRatio: '45% / 55%',
        internationalStudents: '17.0',
        raceEthnicity: {
          'Asian': 20.0,
          'Hispanic': 15.0,
          'White': 35.0,
          'Black': 8.0,
          'Two or more races': 7.0,
          'Nonresident Alien': 17.0,
          'Unknown': 3.0,
        },
      },
    },
  },
  {
    name: 'Dartmouth College',
    location: 'Hanover, NH',
    stats: {
      acceptanceRate: '6.2',
      gpa: { p25: '3.80', p75: '3.98' },
      sat: { p25: 1430, p75: 1550 },
      act: { p25: 32, p75: 35 },
      yieldRate: '61',
      applicationDeadline: 'Jan 3',
      studentFacultyRatio: '7:1',
      fourYearGraduationRate: '88',
      studentCount: '4,556',
      cost: {
        tuitionAndFees: '$64,200',
        roomAndBoard: '$19,800',
        inStateTuition: '$64,200',
      },
      demographics: {
        maleFemaleRatio: '51% / 49%',
        internationalStudents: '14.0',
        raceEthnicity: {
          'Asian': 25.0,
          'Hispanic': 12.0,
          'White': 40.0,
          'Black': 6.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 14.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'University of California, Los Angeles',
    location: 'Los Angeles, CA',
    stats: {
      acceptanceRate: '8.6',
      gpa: { p25: '3.90', p75: '4.00+' },
      sat: { p25: 1400, p75: 1540 },
      act: { p25: 31, p75: 35 },
      yieldRate: '44',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '18:1',
      fourYearGraduationRate: '74',
      studentCount: '32,423',
      cost: {
        tuitionAndFees: '$44,830',
        roomAndBoard: '$19,800',
        inStateTuition: '$13,804',
      },
      demographics: {
        maleFemaleRatio: '41% / 59%',
        internationalStudents: '8.0',
        raceEthnicity: {
          'Asian': 35.0,
          'Hispanic': 25.0,
          'White': 25.0,
          'Black': 3.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 8.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'University of California, Berkeley',
    location: 'Berkeley, CA',
    stats: {
      acceptanceRate: '11.4',
      gpa: { p25: '3.89', p75: '4.00+' },
      sat: { p25: 1350, p75: 1530 },
      act: { p25: 30, p75: 35 },
      yieldRate: '44',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '20:1',
      fourYearGraduationRate: '76',
      studentCount: '32,143',
      cost: {
        tuitionAndFees: '$44,830',
        roomAndBoard: '$19,800',
        inStateTuition: '$14,312',
      },
      demographics: {
        maleFemaleRatio: '46% / 54%',
        internationalStudents: '12.0',
        raceEthnicity: {
          'Asian': 40.0,
          'Hispanic': 20.0,
          'White': 20.0,
          'Black': 3.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 12.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'Rice University',
    location: 'Houston, TX',
    stats: {
      acceptanceRate: '9.0',
      gpa: { p25: '3.80', p75: '4.10' },
      sat: { p25: 1400, p75: 1530 },
      act: { p25: 32, p75: 35 },
      yieldRate: '42',
      applicationDeadline: 'Jan 4',
      studentFacultyRatio: '6:1',
      fourYearGraduationRate: '85',
      studentCount: '4,076',
      cost: {
        tuitionAndFees: '$58,128',
        roomAndBoard: '$19,800',
        inStateTuition: '$58,128',
      },
      demographics: {
        maleFemaleRatio: '52% / 48%',
        internationalStudents: '15.0',
        raceEthnicity: {
          'Asian': 30.0,
          'Hispanic': 15.0,
          'White': 30.0,
          'Black': 7.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 15.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'University of Notre Dame',
    location: 'Notre Dame, IN',
    stats: {
      acceptanceRate: '12.9',
      gpa: { p25: '3.80', p75: '4.10' },
      sat: { p25: 1380, p75: 1520 },
      act: { p25: 32, p75: 35 },
      yieldRate: '58',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '10:1',
      fourYearGraduationRate: '88',
      studentCount: '8,874',
      cost: {
        tuitionAndFees: '$62,693',
        roomAndBoard: '$19,800',
        inStateTuition: '$62,693',
      },
      demographics: {
        maleFemaleRatio: '52% / 48%',
        internationalStudents: '10.0',
        raceEthnicity: {
          'Asian': 15.0,
          'Hispanic': 12.0,
          'White': 55.0,
          'Black': 4.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 10.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'Vanderbilt University',
    location: 'Nashville, TN',
    stats: {
      acceptanceRate: '6.7',
      gpa: { p25: '3.80', p75: '4.10' },
      sat: { p25: 1420, p75: 1540 },
      act: { p25: 32, p75: 35 },
      yieldRate: '47',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '7:1',
      fourYearGraduationRate: '88',
      studentCount: '7,111',
      cost: {
        tuitionAndFees: '$62,320',
        roomAndBoard: '$19,800',
        inStateTuition: '$62,320',
      },
      demographics: {
        maleFemaleRatio: '45% / 55%',
        internationalStudents: '12.0',
        raceEthnicity: {
          'Asian': 20.0,
          'Hispanic': 12.0,
          'White': 45.0,
          'Black': 10.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 12.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'Carnegie Mellon University',
    location: 'Pittsburgh, PA',
    stats: {
      acceptanceRate: '11.3',
      gpa: { p25: '3.80', p75: '4.10' },
      sat: { p25: 1380, p75: 1520 },
      act: { p25: 31, p75: 35 },
      yieldRate: '35',
      applicationDeadline: 'Jan 3',
      studentFacultyRatio: '10:1',
      fourYearGraduationRate: '85',
      studentCount: '7,073',
      cost: {
        tuitionAndFees: '$62,260',
        roomAndBoard: '$19,800',
        inStateTuition: '$62,260',
      },
      demographics: {
        maleFemaleRatio: '55% / 45%',
        internationalStudents: '20.0',
        raceEthnicity: {
          'Asian': 45.0,
          'Hispanic': 10.0,
          'White': 20.0,
          'Black': 5.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 20.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'University of Michigan–Ann Arbor',
    location: 'Ann Arbor, MI',
    type: 'Public',
    stats: {
      acceptanceRate: '18.0',
      gpa: { p25: '3.85', p75: '3.98' },
      sat: { p25: 1340, p75: 1540 },
      act: { p25: 31, p75: 34 },
      yieldRate: '45',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '15:1',
      fourYearGraduationRate: '81',
      studentCount: '32,695',
      cost: {
        tuitionAndFees: '$17,786',
        roomAndBoard: '$13,170',
        inStateTuition: '$17,786',
      },
      demographics: {
        maleFemaleRatio: '50% / 50%',
        internationalStudents: '15.0',
        raceEthnicity: {
          'Asian': 16.0,
          'Hispanic': 7.0,
          'White': 55.0,
          'Black': 4.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 15.0,
          'Unknown': 3.0,
        },
      },
    },
  },
  {
    name: 'Washington University in St. Louis',
    location: 'St. Louis, MO',
    stats: {
      acceptanceRate: '13.0',
      gpa: { p25: '3.85', p75: '4.15' },
      sat: { p25: 1470, p75: 1560 },
      act: { p25: 32, p75: 35 },
      yieldRate: '35',
      applicationDeadline: 'Jan 4',
      studentFacultyRatio: '7:1',
      fourYearGraduationRate: '88',
      studentCount: '7,653',
      cost: {
        tuitionAndFees: '$62,982',
        roomAndBoard: '$19,800',
        inStateTuition: '$62,982',
      },
      demographics: {
        maleFemaleRatio: '47% / 53%',
        internationalStudents: '12.0',
        raceEthnicity: {
          'Asian': 25.0,
          'Hispanic': 8.0,
          'White': 40.0,
          'Black': 6.0,
          'Two or more races': 6.0,
          'Nonresident Alien': 12.0,
          'Unknown': 3.0,
        },
      },
    },
  },
  {
    name: 'Emory University',
    location: 'Atlanta, GA',
    type: 'Private',
    stats: {
      acceptanceRate: '16.2',
      gpa: { p25: '3.80', p75: '4.10' },
      sat: { p25: 1420, p75: 1540 },
      act: { p25: 32, p75: 35 },
      yieldRate: '29',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '9:1',
      fourYearGraduationRate: '84',
      studentCount: '7,101',
      cost: {
        tuitionAndFees: '$60,774',
        roomAndBoard: '$17,400',
        inStateTuition: '$60,774',
      },
      demographics: {
        maleFemaleRatio: '40% / 60%',
        internationalStudents: '17.0',
        raceEthnicity: {
          'Asian': 22.0,
          'Hispanic': 10.0,
          'White': 40.0,
          'Black': 10.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 17.0,
          'Unknown': 6.0,
        },
      },
    },
  },
  {
    name: 'Georgetown University',
    location: 'Washington, DC',
    type: 'Private',
    stats: {
      acceptanceRate: '12.0',
      gpa: { p25: '3.80', p75: '4.10' },
      sat: { p25: 1380, p75: 1550 },
      act: { p25: 32, p75: 35 },
      yieldRate: '44',
      applicationDeadline: 'Jan 10',
      studentFacultyRatio: '11:1',
      fourYearGraduationRate: '89',
      studentCount: '7,900',
      cost: {
        tuitionAndFees: '$64,896',
        roomAndBoard: '$18,600',
        inStateTuition: '$64,896',
      },
      demographics: {
        maleFemaleRatio: '44% / 56%',
        internationalStudents: '12.0',
        raceEthnicity: {
          'Asian': 12.0,
          'Hispanic': 11.0,
          'White': 55.0,
          'Black': 7.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 12.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'University of Virginia',
    location: 'Charlottesville, VA',
    type: 'Public',
    stats: {
      acceptanceRate: '16.2',
      gpa: { p25: '3.85', p75: '4.00+' },
      sat: { p25: 1390, p75: 1530 },
      act: { p25: 32, p75: 35 },
      yieldRate: '41',
      applicationDeadline: 'Jan 5',
      studentFacultyRatio: '15:1',
      fourYearGraduationRate: '89',
      studentCount: '17,311',
      cost: {
        tuitionAndFees: '$23,670',
        roomAndBoard: '$13,950',
        inStateTuition: '$23,670',
      },
      demographics: {
        maleFemaleRatio: '44% / 56%',
        internationalStudents: '8.0',
        raceEthnicity: {
          'Asian': 15.0,
          'Hispanic': 7.0,
          'White': 60.0,
          'Black': 6.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 8.0,
          'Unknown': 4.0,
        },
      },
    },
  },
  {
    name: 'University of North Carolina–Chapel Hill',
    location: 'Chapel Hill, NC',
    type: 'Public',
    stats: {
      acceptanceRate: '17.0',
      gpa: { p25: '3.80', p75: '4.00+' },
      sat: { p25: 1340, p75: 1510 },
      act: { p25: 29, p75: 33 },
      yieldRate: '45',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '15:1',
      fourYearGraduationRate: '84',
      studentCount: '19,743',
      cost: {
        tuitionAndFees: '$9,028',
        roomAndBoard: '$12,492',
        inStateTuition: '$9,028',
      },
      demographics: {
        maleFemaleRatio: '40% / 60%',
        internationalStudents: '6.0',
        raceEthnicity: {
          'Asian': 13.0,
          'Hispanic': 8.0,
          'White': 60.0,
          'Black': 8.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 6.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'University of Southern California',
    location: 'Los Angeles, CA',
    stats: {
      acceptanceRate: '12',
      gpa: { p25: '3.80', p75: '4.10' },
      sat: { p25: 1350, p75: 1530 },
      act: { p25: 31, p75: 34 },
      yieldRate: '39',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '9:1',
      fourYearGraduationRate: '80',
      studentCount: '21,000',
      cost: {
        tuitionAndFees: '$68,237',
        roomAndBoard: '$18,451',
        inStateTuition: '$68,237',
      },
      demographics: {
        maleFemaleRatio: '48% / 52%',
        internationalStudents: '15.0',
        raceEthnicity: {
          'Asian': 20.0,
          'Hispanic': 18.0,
          'White': 35.0,
          'Black': 5.0,
          'Two or more races': 6.0,
          'Nonresident Alien': 15.0,
          'Unknown': 1.0,
        },
      },
    },
  },
  {
    name: 'University of California, San Diego',
    location: 'La Jolla, CA',
    stats: {
      acceptanceRate: '23.7',
      gpa: { p25: '3.80', p75: '4.10' },
      sat: { p25: 1280, p75: 1470 },
      act: { p25: 28, p75: 33 },
      yieldRate: '20',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '19:1',
      fourYearGraduationRate: '87',
      studentCount: '32,906',
      cost: {
        tuitionAndFees: '$14,450',
        roomAndBoard: '$19,800',
        inStateTuition: '$14,450',
      },
      demographics: {
        maleFemaleRatio: '48% / 52%',
        internationalStudents: '8.0',
        raceEthnicity: {
          'Asian': 40.0,
          'Hispanic': 20.0,
          'White': 20.0,
          'Black': 2.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 8.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'New York University',
    location: 'New York, NY',
    type: 'Private',
    stats: {
      acceptanceRate: '13.0',
      gpa: { p25: '3.70', p75: '3.90' },
      sat: { p25: 1370, p75: 1540 },
      act: { p25: 31, p75: 34 },
      yieldRate: '40',
      applicationDeadline: 'Jan 5',
      studentFacultyRatio: '9:1',
      fourYearGraduationRate: '77',
      studentCount: '29,678',
      cost: {
        tuitionAndFees: '$60,438',
        roomAndBoard: '$20,272',
        inStateTuition: '$60,438',
      },
      demographics: {
        maleFemaleRatio: '42% / 58%',
        internationalStudents: '22.0',
        raceEthnicity: {
          'Asian': 20.0,
          'Hispanic': 20.0,
          'White': 32.0,
          'Black': 8.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 22.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'University of Florida',
    location: 'Gainesville, FL',
    type: 'Public',
    stats: {
      acceptanceRate: '23.0',
      gpa: { p25: '3.80', p75: '4.40' },
      sat: { p25: 1300, p75: 1470 },
      act: { p25: 28, p75: 33 },
      yieldRate: '47',
      applicationDeadline: 'Nov 1',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '72',
      studentCount: '34,931',
      cost: {
        tuitionAndFees: '$6,381',
        roomAndBoard: '$10,950',
        inStateTuition: '$6,381',
      },
      demographics: {
        maleFemaleRatio: '44% / 56%',
        internationalStudents: '7.0',
        raceEthnicity: {
          'Asian': 8.0,
          'Hispanic': 23.0,
          'White': 50.0,
          'Black': 7.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 7.0,
          'Unknown': 1.0,
        },
      },
    },
  },
  {
    name: 'University of Texas at Austin',
    location: 'Austin, TX',
    stats: {
      acceptanceRate: '28.8',
      gpa: { p25: '3.75', p75: '4.0' },
      sat: { p25: 1230, p75: 1480 },
      act: { p25: 26, p75: 33 },
      yieldRate: '44',
      applicationDeadline: 'Dec 1',
      studentFacultyRatio: '18:1',
      fourYearGraduationRate: '70',
      studentCount: '40,916',
      cost: {
        tuitionAndFees: '$11,752',
        roomAndBoard: '$12,800',
        inStateTuition: '$11,752',
      },
      demographics: {
        maleFemaleRatio: '47% / 53%',
        internationalStudents: '5.0',
        raceEthnicity: {
          'Asian': 20.0,
          'Hispanic': 25.0,
          'White': 35.0,
          'Black': 4.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 5.0,
          'Unknown': 7.0,
        },
      },
    },
  },
  {
    name: 'Georgia Institute of Technology',
    location: 'Atlanta, GA',
    stats: {
      acceptanceRate: '16.0',
      gpa: { p25: '3.80', p75: '4.10' },
      sat: { p25: 1370, p75: 1530 },
      act: { p25: 31, p75: 34 },
      yieldRate: '38',
      applicationDeadline: 'Jan 4',
      studentFacultyRatio: '19:1',
      fourYearGraduationRate: '42',
      studentCount: '16,561',
      cost: {
        tuitionAndFees: '$12,682',
        roomAndBoard: '$12,800',
        inStateTuition: '$12,682',
      },
      demographics: {
        maleFemaleRatio: '61% / 39%',
        internationalStudents: '8.0',
        raceEthnicity: {
          'Asian': 35.0,
          'Hispanic': 7.0,
          'White': 35.0,
          'Black': 6.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 8.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'University of California, Davis',
    location: 'Davis, CA',
    stats: {
      acceptanceRate: '37.5',
      gpa: { p25: '3.70', p75: '4.0' },
      sat: { p25: 1160, p75: 1400 },
      act: { p25: 25, p75: 31 },
      yieldRate: '18',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '20:1',
      fourYearGraduationRate: '87',
      studentCount: '31,162',
      cost: {
        tuitionAndFees: '$14,450',
        roomAndBoard: '$19,800',
        inStateTuition: '$14,450',
      },
      demographics: {
        maleFemaleRatio: '42% / 58%',
        internationalStudents: '4.0',
        raceEthnicity: {
          'Asian': 35.0,
          'Hispanic': 25.0,
          'White': 25.0,
          'Black': 2.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 4.0,
          'Unknown': 4.0,
        },
      },
    },
  },
  {
    name: 'University of California, Irvine',
    location: 'Irvine, CA',
    stats: {
      acceptanceRate: '21.4',
      gpa: { p25: '3.75', p75: '4.05' },
      sat: { p25: 1260, p75: 1450 },
      act: { p25: 27, p75: 32 },
      yieldRate: '18',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '18:1',
      fourYearGraduationRate: '85',
      studentCount: '29,638',
      cost: {
        tuitionAndFees: '$14,450',
        roomAndBoard: '$19,800',
        inStateTuition: '$14,450',
      },
      demographics: {
        maleFemaleRatio: '47% / 53%',
        internationalStudents: '6.0',
        raceEthnicity: {
          'Asian': 45.0,
          'Hispanic': 20.0,
          'White': 15.0,
          'Black': 2.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 6.0,
          'Unknown': 7.0,
        },
      },
    },
  },
  {
    name: 'University of Illinois Urbana-Champaign',
    location: 'Champaign, IL',
    stats: {
      acceptanceRate: '44.8',
      gpa: { p25: '3.60', p75: '3.95' },
      sat: { p25: 1280, p75: 1480 },
      act: { p25: 27, p75: 32 },
      yieldRate: '27',
      applicationDeadline: 'Jan 5',
      studentFacultyRatio: '20:1',
      fourYearGraduationRate: '85',
      studentCount: '33,915',
      cost: {
        tuitionAndFees: '$17,572',
        roomAndBoard: '$12,800',
        inStateTuition: '$17,572',
      },
      demographics: {
        maleFemaleRatio: '55% / 45%',
        internationalStudents: '10.0',
        raceEthnicity: {
          'Asian': 25.0,
          'Hispanic': 8.0,
          'White': 40.0,
          'Black': 5.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 10.0,
          'Unknown': 8.0,
        },
      },
    },
  },
  {
    name: 'Boston College',
    location: 'Chestnut Hill, MA',
    stats: {
      acceptanceRate: '16.7',
      gpa: { p25: '3.75', p75: '4.0' },
      sat: { p25: 1340, p75: 1500 },
      act: { p25: 31, p75: 34 },
      yieldRate: '35',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '12:1',
      fourYearGraduationRate: '89',
      studentCount: '9,445',
      cost: {
        tuitionAndFees: '$65,202',
        roomAndBoard: '$19,800',
        inStateTuition: '$65,202',
      },
      demographics: {
        maleFemaleRatio: '46% / 54%',
        internationalStudents: '8.0',
        raceEthnicity: {
          'Asian': 15.0,
          'Hispanic': 10.0,
          'White': 50.0,
          'Black': 4.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 8.0,
          'Unknown': 8.0,
        },
      },
    },
  },
  {
    name: 'Tufts University',
    location: 'Medford, MA',
    type: 'Private',
    stats: {
      acceptanceRate: '9.5',
      gpa: { p25: '3.90', p75: '4.20' },
      sat: { p25: 1450, p75: 1550 },
      act: { p25: 33, p75: 35 },
      yieldRate: '45',
      applicationDeadline: 'Jan 4',
      studentFacultyRatio: '10:1',
      fourYearGraduationRate: '89',
      studentCount: '6,114',
      cost: {
        tuitionAndFees: '$67,844',
        roomAndBoard: '$18,000',
        inStateTuition: '$67,844',
      },
      demographics: {
        maleFemaleRatio: '40% / 60%',
        internationalStudents: '12.0',
        raceEthnicity: {
          'Asian': 15.0,
          'Hispanic': 10.0,
          'White': 55.0,
          'Black': 5.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 12.0,
          'Unknown': 3.0,
        },
      },
    },
  },
  {
    name: 'University of California, Santa Barbara',
    location: 'Santa Barbara, CA',
    stats: {
      acceptanceRate: '25.9',
      gpa: { p25: '3.80', p75: '4.10' },
      sat: { p25: 1280, p75: 1480 },
      act: { p25: 28, p75: 33 },
      yieldRate: '20',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '81',
      studentCount: '23,196',
      cost: {
        tuitionAndFees: '$14,450',
        roomAndBoard: '$19,800',
        inStateTuition: '$14,450',
      },
      demographics: {
        maleFemaleRatio: '44% / 56%',
        internationalStudents: '6.0',
        raceEthnicity: {
          'Asian': 25.0,
          'Hispanic': 25.0,
          'White': 30.0,
          'Black': 2.0,
          'Two or more races': 6.0,
          'Nonresident Alien': 6.0,
          'Unknown': 6.0,
        },
      },
    },
  },
  {
    name: 'University of Wisconsin-Madison',
    location: 'Madison, WI',
    stats: {
      acceptanceRate: '49.5',
      gpa: { p25: '3.75', p75: '4.0' },
      sat: { p25: 1300, p75: 1480 },
      act: { p25: 27, p75: 32 },
      yieldRate: '26',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '87',
      studentCount: '33,585',
      cost: {
        tuitionAndFees: '$10,796',
        roomAndBoard: '$12,800',
        inStateTuition: '$10,796',
      },
      demographics: {
        maleFemaleRatio: '47% / 53%',
        internationalStudents: '6.0',
        raceEthnicity: {
          'Asian': 8.0,
          'Hispanic': 5.0,
          'White': 65.0,
          'Black': 2.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 6.0,
          'Unknown': 10.0,
        },
      },
    },
  },
  {
    name: 'Boston University',
    location: 'Boston, MA',
    stats: {
      acceptanceRate: '14.4',
      gpa: { p25: '3.70', p75: '4.0' },
      sat: { p25: 1340, p75: 1510 },
      act: { p25: 30, p75: 34 },
      yieldRate: '27',
      applicationDeadline: 'Jan 4',
      studentFacultyRatio: '11:1',
      fourYearGraduationRate: '85',
      studentCount: '18,459',
      cost: {
        tuitionAndFees: '$65,168',
        roomAndBoard: '$19,800',
        inStateTuition: '$65,168',
      },
      demographics: {
        maleFemaleRatio: '40% / 60%',
        internationalStudents: '18.0',
        raceEthnicity: {
          'Asian': 20.0,
          'Hispanic': 10.0,
          'White': 35.0,
          'Black': 4.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 18.0,
          'Unknown': 8.0,
        },
      },
    },
  },
  {
    name: 'Ohio State University',
    location: 'Columbus, OH',
    stats: {
      acceptanceRate: '53.7',
      gpa: { p25: '3.60', p75: '3.95' },
      sat: { p25: 1250, p75: 1440 },
      act: { p25: 26, p75: 31 },
      yieldRate: '25',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '19:1',
      fourYearGraduationRate: '84',
      studentCount: '46,984',
      cost: {
        tuitionAndFees: '$12,485',
        roomAndBoard: '$13,200',
        inStateTuition: '$12,485',
      },
      demographics: {
        maleFemaleRatio: '48% / 52%',
        internationalStudents: '5.0',
        raceEthnicity: {
          'Asian': 8.0,
          'Hispanic': 4.0,
          'White': 65.0,
          'Black': 6.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 5.0,
          'Unknown': 8.0,
        },
      },
    },
  },
  {
    name: 'Rutgers University',
    location: 'New Brunswick, NJ',
    stats: {
      acceptanceRate: '66.3',
      gpa: { p25: '3.50', p75: '3.90' },
      sat: { p25: 1180, p75: 1410 },
      act: { p25: 25, p75: 31 },
      yieldRate: '25',
      applicationDeadline: 'Dec 1',
      studentFacultyRatio: '16:1',
      fourYearGraduationRate: '80',
      studentCount: '36,344',
      cost: {
        tuitionAndFees: '$16,263',
        roomAndBoard: '$14,400',
        inStateTuition: '$16,263',
      },
      demographics: {
        maleFemaleRatio: '48% / 52%',
        internationalStudents: '6.0',
        raceEthnicity: {
          'Asian': 30.0,
          'Hispanic': 12.0,
          'White': 35.0,
          'Black': 7.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 6.0,
          'Unknown': 6.0,
        },
      },
    },
  },
  {
    name: 'University of Maryland',
    location: 'College Park, MD',
    stats: {
      acceptanceRate: '44.2',
      gpa: { p25: '3.70', p75: '4.0' },
      sat: { p25: 1280, p75: 1470 },
      act: { p25: 28, p75: 33 },
      yieldRate: '28',
      applicationDeadline: 'Jan 20',
      studentFacultyRatio: '18:1',
      fourYearGraduationRate: '85',
      studentCount: '30,875',
      cost: {
        tuitionAndFees: '$11,233',
        roomAndBoard: '$13,200',
        inStateTuition: '$11,233',
      },
      demographics: {
        maleFemaleRatio: '49% / 51%',
        internationalStudents: '8.0',
        raceEthnicity: {
          'Asian': 25.0,
          'Hispanic': 8.0,
          'White': 40.0,
          'Black': 10.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 8.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'University of Rochester',
    location: 'Rochester, NY',
    stats: {
      acceptanceRate: '35.5',
      gpa: { p25: '3.70', p75: '4.0' },
      sat: { p25: 1320, p75: 1500 },
      act: { p25: 30, p75: 33 },
      yieldRate: '20',
      applicationDeadline: 'Jan 5',
      studentFacultyRatio: '10:1',
      fourYearGraduationRate: '85',
      studentCount: '6,780',
      cost: {
        tuitionAndFees: '$62,678',
        roomAndBoard: '$19,800',
        inStateTuition: '$62,678',
      },
      demographics: {
        maleFemaleRatio: '47% / 53%',
        internationalStudents: '15.0',
        raceEthnicity: {
          'Asian': 20.0,
          'Hispanic': 8.0,
          'White': 40.0,
          'Black': 5.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 15.0,
          'Unknown': 7.0,
        },
      },
    },
  },
  {
    name: 'Lehigh University',
    location: 'Bethlehem, PA',
    stats: {
      acceptanceRate: '32.1',
      gpa: { p25: '3.65', p75: '3.95' },
      sat: { p25: 1320, p75: 1490 },
      act: { p25: 30, p75: 33 },
      yieldRate: '25',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '10:1',
      fourYearGraduationRate: '85',
      studentCount: '5,624',
      cost: {
        tuitionAndFees: '$62,180',
        roomAndBoard: '$19,800',
        inStateTuition: '$62,180',
      },
      demographics: {
        maleFemaleRatio: '55% / 45%',
        internationalStudents: '10.0',
        raceEthnicity: {
          'Asian': 15.0,
          'Hispanic': 8.0,
          'White': 50.0,
          'Black': 4.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 10.0,
          'Unknown': 8.0,
        },
      },
    },
  },
  {
    name: 'Purdue University',
    location: 'West Lafayette, IN',
    stats: {
      acceptanceRate: '52.7',
      gpa: { p25: '3.50', p75: '3.90' },
      sat: { p25: 1190, p75: 1410 },
      act: { p25: 25, p75: 32 },
      yieldRate: '25',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '65',
      studentCount: '33,646',
      cost: {
        tuitionAndFees: '$9,992',
        roomAndBoard: '$11,200',
        inStateTuition: '$9,992',
      },
      demographics: {
        maleFemaleRatio: '58% / 42%',
        internationalStudents: '12.0',
        raceEthnicity: {
          'Asian': 15.0,
          'Hispanic': 5.0,
          'White': 55.0,
          'Black': 3.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 12.0,
          'Unknown': 6.0,
        },
      },
    },
  },
  {
    name: 'University of Georgia',
    location: 'Athens, GA',
    type: 'Public',
    stats: {
      acceptanceRate: '37.0',
      gpa: { p25: '4.08', p75: '4.35' },
      sat: { p25: 1300, p75: 1470 },
      act: { p25: 30, p75: 34 },
      yieldRate: '29',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '72',
      studentCount: '31,514',
      cost: {
        tuitionAndFees: '$29,832',
        roomAndBoard: '$12,230',
        inStateTuition: '$11,830',
      },
      demographics: {
        maleFemaleRatio: '43% / 57%',
        internationalStudents: '4.2',
        raceEthnicity: {
          'Asian': 10.5,
          'Hispanic': 7.8,
          'White': 66.2,
          'Black': 8.3,
          'Two or more races': 3.0,
          'Nonresident Alien': 4.2,
          'Unknown': 0.0,
        },
      },
    },
  },
  {
    name: 'University of Washington',
    location: 'Seattle, WA',
    type: 'Public',
    stats: {
      acceptanceRate: '42.0',
      gpa: { p25: '3.74', p75: '3.98' },
      sat: { p25: 1280, p75: 1490 },
      act: { p25: 30, p75: 34 },
      yieldRate: '24',
      applicationDeadline: 'Nov 15',
      studentFacultyRatio: '19:1',
      fourYearGraduationRate: '78',
      studentCount: '39,125',
      cost: {
        tuitionAndFees: '$39,906',
        roomAndBoard: '$14,526',
        inStateTuition: '$12,092',
      },
      demographics: {
        maleFemaleRatio: '46% / 54%',
        internationalStudents: '12.0',
        raceEthnicity: {
          'Asian': 26.0,
          'Hispanic': 8.5,
          'White': 40.5,
          'Black': 3.5,
          'Two or more races': 7.5,
          'Nonresident Alien': 12.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'Wake Forest University',
    location: 'Winston-Salem, NC',
    type: 'Private',
    stats: {
      acceptanceRate: '22.0',
      gpa: { p25: '3.90', p75: '4.15' },
      sat: { p25: 1410, p75: 1500 },
      act: { p25: 32, p75: 34 },
      yieldRate: '32',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '10:1',
      fourYearGraduationRate: '88',
      studentCount: '5,490',
      cost: {
        tuitionAndFees: '$64,758',
        roomAndBoard: '$17,890',
        inStateTuition: '$64,758',
      },
      demographics: {
        maleFemaleRatio: '47% / 53%',
        internationalStudents: '8.0',
        raceEthnicity: {
          'Asian': 9.8,
          'Hispanic': 8.5,
          'White': 63.2,
          'Black': 7.1,
          'Two or more races': 3.4,
          'Nonresident Alien': 8.0,
          'Unknown': 0.0,
        },
      },
    },
  },
  {
    name: 'Case Western Reserve University',
    location: 'Cleveland, OH',
    type: 'Private',
    stats: {
      acceptanceRate: '29.0',
      gpa: { p25: '3.75', p75: '3.96' },
      sat: { p25: 1440, p75: 1520 },
      act: { p25: 32, p75: 34 },
      yieldRate: '14',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '9:1',
      fourYearGraduationRate: '78',
      studentCount: '6,528',
      cost: {
        tuitionAndFees: '$68,660',
        roomAndBoard: '$19,514',
        inStateTuition: '$68,660',
      },
      demographics: {
        maleFemaleRatio: '52% / 48%',
        internationalStudents: '16.0',
        raceEthnicity: {
          'Asian': 30.1,
          'Hispanic': 11.5,
          'White': 33.7,
          'Black': 6.2,
          'Two or more races': 5.2,
          'Nonresident Alien': 16.0,
          'Unknown': 1.6,
        },
      },
    },
  },
  {
    name: 'Texas A&M University',
    location: 'College Station, TX',
    type: 'Public',
    stats: {
      acceptanceRate: '57.0',
      gpa: { p25: '3.40', p75: '3.95' },
      sat: { p25: 1160, p75: 1390 },
      act: { p25: 25, p75: 31 },
      yieldRate: '48',
      applicationDeadline: 'Dec 1',
      studentFacultyRatio: '21:1',
      fourYearGraduationRate: '65',
      studentCount: '60,710',
      cost: {
        tuitionAndFees: '$40,157',
        roomAndBoard: '$13,008',
        inStateTuition: '$12,856',
      },
      demographics: {
        maleFemaleRatio: '52% / 48%',
        internationalStudents: '1.2',
        raceEthnicity: {
          'Asian': 13.7,
          'Hispanic': 25.7,
          'White': 52.0,
          'Black': 2.5,
          'Two or more races': 3.8,
          'Nonresident Alien': 1.2,
          'Unknown': 0.7,
        },
      },
    },
  },
  {
    name: 'Virginia Tech',
    location: 'Blacksburg, VA',
    type: 'Public',
    stats: {
      acceptanceRate: '57.0',
      gpa: { p25: '3.70', p75: '4.00+' },
      sat: { p25: 1240, p75: 1410 },
      act: { p25: 27, p75: 32 },
      yieldRate: '30',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '69',
      studentCount: '31,035',
      cost: {
        tuitionAndFees: '$36,158',
        roomAndBoard: '$16,550',
        inStateTuition: '$15,948',
      },
      demographics: {
        maleFemaleRatio: '56% / 44%',
        internationalStudents: '4.5',
        raceEthnicity: {
          'Asian': 13.6,
          'Hispanic': 9.8,
          'White': 57.0,
          'Black': 5.8,
          'Two or more races': 5.7,
          'Nonresident Alien': 4.7,
          'Unknown': 3.1,
        },
      },
    },
  },
  {
    name: 'Florida State University',
    location: 'Tallahassee, FL',
    type: 'Public',
    stats: {
      acceptanceRate: '22.3',
      gpa: { p25: '4.10', p75: '4.50' },
      sat: { p25: 1350, p75: 1430 },
      act: { p25: 30, p75: 32 },
      yieldRate: '23.2',
      applicationDeadline: 'Mar 1',
      studentFacultyRatio: '21:1',
      fourYearGraduationRate: '85',
      studentCount: '44,308',
      cost: {
        tuitionAndFees: '$19,806',
        roomAndBoard: '$12,500',
        inStateTuition: '$4,640',
      },
      demographics: {
        maleFemaleRatio: '43% / 57%',
        internationalStudents: '4.0',
        raceEthnicity: {
          'Asian': 3.8,
          'Hispanic': 20.9,
          'White': 54.7,
          'Black': 8.4,
          'Two or more races': 4.2,
          'Nonresident Alien': 4.0,
          'Unknown': 4.0,
        },
      },
    },
  },
  {
    name: 'Northeastern University',
    location: 'Boston, MA',
    type: 'Private',
    stats: {
      acceptanceRate: '5.2',
      gpa: { p25: '3.90', p75: '4.00+' },
      sat: { p25: 1450, p75: 1500 },
      act: { p25: 33, p75: 35 },
      yieldRate: '28',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '16:1',
      fourYearGraduationRate: '88',
      studentCount: '22,695',
      cost: {
        tuitionAndFees: '$64,990',
        roomAndBoard: '$21,620',
        inStateTuition: '$64,990',
      },
      demographics: {
        maleFemaleRatio: '48% / 52%',
        internationalStudents: '18.0',
        raceEthnicity: {
          'Asian': 22.4,
          'Hispanic': 10.4,
          'White': 40.6,
          'Black': 4.8,
          'Two or more races': 5.8,
          'Nonresident Alien': 18.0,
          'Unknown': 3.0,
        },
      },
    },
  },
  {
    name: 'University of Minnesota Twin Cities',
    location: 'Minneapolis, MN',
    type: 'Public',
    stats: {
      acceptanceRate: '77.0',
      gpa: { p25: '3.52', p75: '3.96' },
      sat: { p25: 1350, p75: 1500 },
      act: { p25: 27, p75: 33 },
      yieldRate: '22',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '78',
      studentCount: '39,556',
      cost: {
        tuitionAndFees: '$34,410',
        roomAndBoard: '$13,500',
        inStateTuition: '$14,496',
      },
      demographics: {
        maleFemaleRatio: '46% / 54%',
        internationalStudents: '10.0',
        raceEthnicity: {
          'Asian': 11.0,
          'Hispanic': 6.0,
          'White': 56.0,
          'Black': 7.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 10.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'College of William & Mary',
    location: 'Williamsburg, VA',
    type: 'Public',
    stats: {
      acceptanceRate: '34.0',
      gpa: { p25: '4.18', p75: '4.53' },
      sat: { p25: 1400, p75: 1530 },
      act: { p25: 32, p75: 34 },
      yieldRate: '31',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '12:1',
      fourYearGraduationRate: '91',
      studentCount: '7,063',
      cost: {
        tuitionAndFees: '$43,442',
        roomAndBoard: '$13,500',
        inStateTuition: '$18,709',
      },
      demographics: {
        maleFemaleRatio: '42% / 58%',
        internationalStudents: '5.0',
        raceEthnicity: {
          'Asian': 12.1,
          'Hispanic': 9.3,
          'White': 63.1,
          'Black': 6.1,
          'Two or more races': 4.4,
          'Nonresident Alien': 5.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'North Carolina State University',
    location: 'Raleigh, NC',
    type: 'Public',
    stats: {
      acceptanceRate: '47.0',
      gpa: { p25: '3.75', p75: '4.00+' },
      sat: { p25: 1270, p75: 1420 },
      act: { p25: 27, p75: 32 },
      yieldRate: '36',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '14:1',
      fourYearGraduationRate: '62',
      studentCount: '26,254',
      cost: {
        tuitionAndFees: '$9,128',
        roomAndBoard: '$12,748',
        inStateTuition: '$9,128',
      },
      demographics: {
        maleFemaleRatio: '54% / 46%',
        internationalStudents: '5.0',
        raceEthnicity: {
          'Asian': 7.0,
          'Hispanic': 7.0,
          'White': 70.0,
          'Black': 6.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 5.0,
          'Unknown': 1.0,
        },
      },
    },
  },
  {
    name: 'Stony Brook University (SUNY)',
    location: 'Stony Brook, NY',
    type: 'Public',
    stats: {
      acceptanceRate: '48.0',
      gpa: { p25: '3.60', p75: '3.90' },
      sat: { p25: 1230, p75: 1440 },
      act: { p25: 26, p75: 32 },
      yieldRate: '20',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '19:1',
      fourYearGraduationRate: '62',
      studentCount: '18,010',
      cost: {
        tuitionAndFees: '$10,410',
        roomAndBoard: '$15,370',
        inStateTuition: '$10,410',
      },
      demographics: {
        maleFemaleRatio: '51% / 49%',
        internationalStudents: '15.0',
        raceEthnicity: {
          'Asian': 36.0,
          'Hispanic': 14.0,
          'White': 28.0,
          'Black': 8.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 15.0,
          'Unknown': 7.0,
        },
      },
    },
  },
  {
    name: 'University of California, Merced',
    location: 'Merced, CA',
    type: 'Public',
    stats: {
      acceptanceRate: '85.0',
      gpa: { p25: '3.40', p75: '3.80' },
      sat: { p25: 970, p75: 1160 },
      act: { p25: 17, p75: 24 },
      yieldRate: '10',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '20:1',
      fourYearGraduationRate: '38',
      studentCount: '8,321',
      cost: {
        tuitionAndFees: '$13,791',
        roomAndBoard: '$18,000',
        inStateTuition: '$13,791',
      },
      demographics: {
        maleFemaleRatio: '48% / 52%',
        internationalStudents: '2.0',
        raceEthnicity: {
          'Asian': 13.0,
          'Hispanic': 54.0,
          'White': 13.0,
          'Black': 4.0,
          'Two or more races': 3.0,
          'Nonresident Alien': 2.0,
          'Unknown': 11.0,
        },
      },
    },
  },
  {
    name: 'University of Massachusetts Amherst',
    location: 'Amherst, MA',
    type: 'Public',
    stats: {
      acceptanceRate: '63.0',
      gpa: { p25: '3.70', p75: '4.00' },
      sat: { p25: 1200, p75: 1390 },
      act: { p25: 27, p75: 32 },
      yieldRate: '22',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '67',
      studentCount: '24,231',
      cost: {
        tuitionAndFees: '$17,357',
        roomAndBoard: '$13,004',
        inStateTuition: '$17,357',
      },
      demographics: {
        maleFemaleRatio: '50% / 50%',
        internationalStudents: '8.0',
        raceEthnicity: {
          'Asian': 13.0,
          'Hispanic': 10.0,
          'White': 60.0,
          'Black': 5.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 8.0,
          'Unknown': 8.0,
        },
      },
    },
  },
  {
    name: 'Villanova University',
    location: 'Villanova, PA',
    type: 'Private',
    stats: {
      acceptanceRate: '23.0',
      gpa: { p25: '3.80', p75: '4.10' },
      sat: { p25: 1320, p75: 1470 },
      act: { p25: 31, p75: 34 },
      yieldRate: '24',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '11:1',
      fourYearGraduationRate: '89',
      studentCount: '7,037',
      cost: {
        tuitionAndFees: '$64,700',
        roomAndBoard: '$15,000',
        inStateTuition: '$64,700',
      },
      demographics: {
        maleFemaleRatio: '46% / 54%',
        internationalStudents: '3.0',
        raceEthnicity: {
          'Asian': 6.0,
          'Hispanic': 7.0,
          'White': 75.0,
          'Black': 5.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 3.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'Brandeis University',
    location: 'Waltham, MA',
    type: 'Private',
    stats: {
      acceptanceRate: '39.0',
      gpa: { p25: '3.70', p75: '4.00' },
      sat: { p25: 1340, p75: 1510 },
      act: { p25: 31, p75: 34 },
      yieldRate: '20',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '10:1',
      fourYearGraduationRate: '85',
      studentCount: '3,591',
      cost: {
        tuitionAndFees: '$64,706',
        roomAndBoard: '$17,000',
        inStateTuition: '$64,706',
      },
      demographics: {
        maleFemaleRatio: '40% / 60%',
        internationalStudents: '20.0',
        raceEthnicity: {
          'Asian': 15.0,
          'Hispanic': 8.0,
          'White': 50.0,
          'Black': 5.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 20.0,
          'Unknown': 2.0,
        },
      },
    },
  },
  {
    name: 'George Washington University',
    location: 'Washington, DC',
    type: 'Private',
    stats: {
      acceptanceRate: '49.0',
      gpa: { p25: '3.60', p75: '3.90' },
      sat: { p25: 1280, p75: 1460 },
      act: { p25: 30, p75: 33 },
      yieldRate: '20',
      applicationDeadline: 'Jan 5',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '80',
      studentCount: '11,482',
      cost: {
        tuitionAndFees: '$64,700',
        roomAndBoard: '$15,000',
        inStateTuition: '$64,700',
      },
      demographics: {
        maleFemaleRatio: '38% / 62%',
        internationalStudents: '12.0',
        raceEthnicity: {
          'Asian': 10.0,
          'Hispanic': 10.0,
          'White': 60.0,
          'Black': 7.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 12.0,
          'Unknown': 6.0,
        },
      },
    },
  },
  {
    name: 'Michigan State University',
    location: 'East Lansing, MI',
    type: 'Public',
    stats: {
      acceptanceRate: '83.0',
      gpa: { p25: '3.50', p75: '3.90' },
      sat: { p25: 1100, p75: 1320 },
      act: { p25: 23, p75: 29 },
      yieldRate: '27',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '16:1',
      fourYearGraduationRate: '59',
      studentCount: '38,574',
      cost: {
        tuitionAndFees: '$15,372',
        roomAndBoard: '$10,676',
        inStateTuition: '$15,372',
      },
      demographics: {
        maleFemaleRatio: '48% / 52%',
        internationalStudents: '8.0',
        raceEthnicity: {
          'Asian': 7.0,
          'Hispanic': 7.0,
          'White': 65.0,
          'Black': 7.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 8.0,
          'Unknown': 9.0,
        },
      },
    },
  },
  {
    name: 'The Pennsylvania State University–University Park',
    location: 'University Park, PA',
    type: 'Public',
    stats: {
      acceptanceRate: '55.0',
      gpa: { p25: '3.60', p75: '3.90' },
      sat: { p25: 1160, p75: 1360 },
      act: { p25: 25, p75: 31 },
      yieldRate: '20',
      applicationDeadline: 'Nov 1',
      studentFacultyRatio: '15:1',
      fourYearGraduationRate: '68',
      studentCount: '40,639',
      cost: {
        tuitionAndFees: '$19,835',
        roomAndBoard: '$12,984',
        inStateTuition: '$19,835',
      },
      demographics: {
        maleFemaleRatio: '53% / 47%',
        internationalStudents: '10.0',
        raceEthnicity: {
          'Asian': 7.0,
          'Hispanic': 7.0,
          'White': 65.0,
          'Black': 5.0,
          'Two or more races': 4.0,
          'Nonresident Alien': 10.0,
          'Unknown': 9.0,
        },
      },
    },
  },
  {
    name: 'Santa Clara University',
    location: 'Santa Clara, CA',
    type: 'Private',
    stats: {
      acceptanceRate: '54.0',
      gpa: { p25: '3.60', p75: '3.90' },
      sat: { p25: 1290, p75: 1460 },
      act: { p25: 29, p75: 33 },
      yieldRate: '20',
      applicationDeadline: 'Jan 7',
      studentFacultyRatio: '10:1',
      fourYearGraduationRate: '86',
      studentCount: '5,895',
      cost: {
        tuitionAndFees: '$58,587',
        roomAndBoard: '$16,779',
        inStateTuition: '$58,587',
      },
      demographics: {
        maleFemaleRatio: '48% / 52%',
        internationalStudents: '7.0',
        raceEthnicity: {
          'Asian': 22.0,
          'Hispanic': 18.0,
          'White': 40.0,
          'Black': 3.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 7.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'Tulane University of Louisiana',
    location: 'New Orleans, LA',
    type: 'Private',
    stats: {
      acceptanceRate: '13.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1340, p75: 1490 },
      act: { p25: 30, p75: 33 },
      yieldRate: '28',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '8:1',
      fourYearGraduationRate: '77',
      studentCount: '8,610',
      cost: {
        tuitionAndFees: '$64,264',
        roomAndBoard: '$16,784',
        inStateTuition: '$64,264',
      },
      demographics: {
        maleFemaleRatio: '41% / 59%',
        internationalStudents: '6.0',
        raceEthnicity: {
          'Asian': 6.0,
          'Hispanic': 8.0,
          'White': 70.0,
          'Black': 7.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 6.0,
          'Unknown': 4.0,
        },
      },
    },
  },
  {
    name: 'University of Miami',
    location: 'Coral Gables, FL',
    type: 'Private',
    stats: {
      acceptanceRate: '19.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1300, p75: 1460 },
      act: { p25: 29, p75: 33 },
      yieldRate: '17',
      applicationDeadline: 'Jan 1',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '70',
      studentCount: '12,504',
      cost: {
        tuitionAndFees: '$57,194',
        roomAndBoard: '$16,264',
        inStateTuition: '$57,194',
      },
      demographics: {
        maleFemaleRatio: '45% / 55%',
        internationalStudents: '13.0',
        raceEthnicity: {
          'Asian': 7.0,
          'Hispanic': 27.0,
          'White': 40.0,
          'Black': 8.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 13.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'Rensselaer Polytechnic Institute',
    location: 'Troy, NY',
    type: 'Private',
    stats: {
      acceptanceRate: '57.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1290, p75: 1490 },
      act: { p25: 28, p75: 33 },
      yieldRate: '17',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '74',
      studentCount: '5,570',
      cost: {
        tuitionAndFees: '$60,051',
        roomAndBoard: '$16,480',
        inStateTuition: '$60,051',
      },
      demographics: {
        maleFemaleRatio: '68% / 32%',
        internationalStudents: '13.0',
        raceEthnicity: {
          'Asian': 13.0,
          'Hispanic': 8.0,
          'White': 50.0,
          'Black': 4.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 13.0,
          'Unknown': 7.0,
        },
      },
    },
  },
  {
    name: 'University of Connecticut',
    location: 'Storrs, CT',
    type: 'Public',
    stats: {
      acceptanceRate: '56.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1200, p75: 1390 },
      act: { p25: 27, p75: 32 },
      yieldRate: '23',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '16:1',
      fourYearGraduationRate: '72',
      studentCount: '18,917',
      cost: {
        tuitionAndFees: '$18,524',
        roomAndBoard: '$13,258',
        inStateTuition: '$18,524',
      },
      demographics: {
        maleFemaleRatio: '48% / 52%',
        internationalStudents: '8.0',
        raceEthnicity: {
          'Asian': 13.0,
          'Hispanic': 13.0,
          'White': 55.0,
          'Black': 7.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 8.0,
          'Unknown': 7.0,
        },
      },
    },
  },
  {
    name: 'University of Pittsburgh',
    location: 'Pittsburgh, PA',
    type: 'Public',
    stats: {
      acceptanceRate: '49.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1250, p75: 1440 },
      act: { p25: 28, p75: 33 },
      yieldRate: '22',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '14:1',
      fourYearGraduationRate: '68',
      studentCount: '19,197',
      cost: {
        tuitionAndFees: '$21,080',
        roomAndBoard: '$12,268',
        inStateTuition: '$21,080',
      },
      demographics: {
        maleFemaleRatio: '44% / 56%',
        internationalStudents: '7.0',
        raceEthnicity: {
          'Asian': 11.0,
          'Hispanic': 6.0,
          'White': 70.0,
          'Black': 6.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 7.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'Binghamton University (SUNY)',
    location: 'Binghamton, NY',
    type: 'Public',
    stats: {
      acceptanceRate: '44.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1290, p75: 1450 },
      act: { p25: 29, p75: 33 },
      yieldRate: '22',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '19:1',
      fourYearGraduationRate: '72',
      studentCount: '14,307',
      cost: {
        tuitionAndFees: '$10,390',
        roomAndBoard: '$18,000',
        inStateTuition: '$10,390',
      },
      demographics: {
        maleFemaleRatio: '49% / 51%',
        internationalStudents: '8.0',
        raceEthnicity: {
          'Asian': 18.0,
          'Hispanic': 10.0,
          'White': 55.0,
          'Black': 5.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 8.0,
          'Unknown': 4.0,
        },
      },
    },
  },
  {
    name: 'Indiana University–Bloomington',
    location: 'Bloomington, IN',
    type: 'Public',
    stats: {
      acceptanceRate: '82.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1170, p75: 1380 },
      act: { p25: 26, p75: 32 },
      yieldRate: '25',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '16:1',
      fourYearGraduationRate: '65',
      studentCount: '32,986',
      cost: {
        tuitionAndFees: '$11,790',
        roomAndBoard: '$12,098',
        inStateTuition: '$11,790',
      },
      demographics: {
        maleFemaleRatio: '48% male / 52% female',
        internationalStudents: '8',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Syracuse University',
    location: 'Syracuse, NY',
    type: 'Private',
    stats: {
      acceptanceRate: '59.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1180, p75: 1370 },
      act: { p25: 26, p75: 31 },
      yieldRate: '20',
      applicationDeadline: 'Jan 5',
      studentFacultyRatio: '15:1',
      fourYearGraduationRate: '70',
      studentCount: '14,479',
      cost: {
        tuitionAndFees: '$60,135',
        roomAndBoard: '$17,436',
        inStateTuition: '$60,135',
      },
      demographics: {
        maleFemaleRatio: '46% male / 54% female',
        internationalStudents: '13',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Colorado School of Mines',
    location: 'Golden, CO',
    type: 'Public',
    stats: {
      acceptanceRate: '57.0',
      gpa: { p25: '3.80', p75: '4.00' },
      sat: { p25: 1310, p75: 1460 },
      act: { p25: 29, p75: 33 },
      yieldRate: '28',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '15:1',
      fourYearGraduationRate: '64',
      studentCount: '5,216',
      cost: {
        tuitionAndFees: '$19,100',
        roomAndBoard: '$16,100',
        inStateTuition: '$19,100',
      },
      demographics: {
        maleFemaleRatio: '69% male / 31% female',
        internationalStudents: '7',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Stevens Institute of Technology',
    location: 'Hoboken, NJ',
    type: 'Private',
    stats: {
      acceptanceRate: '53.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1340, p75: 1500 },
      act: { p25: 31, p75: 34 },
      yieldRate: '22',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '11:1',
      fourYearGraduationRate: '66',
      studentCount: '4,131',
      cost: {
        tuitionAndFees: '$58,624',
        roomAndBoard: '$16,244',
        inStateTuition: '$58,624',
      },
      demographics: {
        maleFemaleRatio: '70% male / 30% female',
        internationalStudents: '17',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University at Buffalo (SUNY)',
    location: 'Buffalo, NY',
    type: 'Public',
    stats: {
      acceptanceRate: '70.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1160, p75: 1340 },
      act: { p25: 24, p75: 30 },
      yieldRate: '20',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '14:1',
      fourYearGraduationRate: '61',
      studentCount: '22,307',
      cost: {
        tuitionAndFees: '$10,782',
        roomAndBoard: '$15,978',
        inStateTuition: '$10,782',
      },
      demographics: {
        maleFemaleRatio: '51% male / 49% female',
        internationalStudents: '13',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of California, Riverside',
    location: 'Riverside, CA',
    type: 'Public',
    stats: {
      acceptanceRate: '69.0',
      gpa: { p25: '3.80', p75: '4.18' },
      sat: { p25: 1130, p75: 1400 },
      act: { p25: 24, p75: 33 },
      yieldRate: '17',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '23:1',
      fourYearGraduationRate: '56',
      studentCount: '22,055',
      cost: {
        tuitionAndFees: '$13,754',
        roomAndBoard: '$17,200',
        inStateTuition: '$13,754',
      },
      demographics: {
        maleFemaleRatio: '44% male / 56% female',
        internationalStudents: '8',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Pepperdine University',
    location: 'Malibu, CA',
    type: 'Private',
    stats: {
      acceptanceRate: '49.0',
      gpa: { p25: '3.60', p75: '3.90' },
      sat: { p25: 1230, p75: 1450 },
      act: { p25: 27, p75: 32 },
      yieldRate: '22',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '77',
      studentCount: '3,708',
      cost: {
        tuitionAndFees: '$65,990',
        roomAndBoard: '$18,120',
        inStateTuition: '$65,990',
      },
      demographics: {
        maleFemaleRatio: '41% male / 59% female',
        internationalStudents: '7',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Rutgers University–Newark',
    location: 'Newark, NJ',
    type: 'Public',
    stats: {
      acceptanceRate: '77.0',
      gpa: { p25: '3.10', p75: '3.80' },
      sat: { p25: 990, p75: 1200 },
      act: { p25: 19, p75: 26 },
      yieldRate: '20',
      applicationDeadline: 'Dec 1',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '34',
      studentCount: '8,726',
      cost: {
        tuitionAndFees: '$15,648',
        roomAndBoard: '$13,402',
        inStateTuition: '$15,648',
      },
      demographics: {
        maleFemaleRatio: '41% male / 59% female',
        internationalStudents: '6',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Illinois Chicago',
    location: 'Chicago, IL',
    type: 'Public',
    stats: {
      acceptanceRate: '60.0',
      gpa: { p25: '3.50', p75: '3.90' },
      sat: { p25: 1170, p75: 1360 },
      act: { p25: 24, p75: 30 },
      yieldRate: '20',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '50',
      studentCount: '28,000',
      cost: {
        tuitionAndFees: '$15,372',
        roomAndBoard: '$10,676',
        inStateTuition: '$15,372',
      },
      demographics: {
        maleFemaleRatio: '41% male / 59% female',
        internationalStudents: '6',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'New Jersey Institute of Technology',
    location: 'Newark, NJ',
    type: 'Public',
    stats: {
      acceptanceRate: '66.0',
      gpa: { p25: '3.30', p75: '3.90' },
      sat: { p25: 1190, p75: 1390 },
      act: { p25: 25, p75: 32 },
      yieldRate: '22',
      applicationDeadline: 'Mar 1',
      studentFacultyRatio: '15:1',
      fourYearGraduationRate: '38',
      studentCount: '8,477',
      cost: {
        tuitionAndFees: '$19,706',
        roomAndBoard: '$13,600',
        inStateTuition: '$19,706',
      },
      demographics: {
        maleFemaleRatio: '74% male / 26% female',
        internationalStudents: '14',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of California, Santa Cruz',
    location: 'Santa Cruz, CA',
    type: 'Public',
    stats: {
      acceptanceRate: '47.1',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1200, p75: 1410 },
      act: { p25: 26, p75: 32 },
      yieldRate: '15',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '19:1',
      fourYearGraduationRate: '62',
      studentCount: '17,207',
      cost: {
        tuitionAndFees: '$14,070',
        roomAndBoard: '$17,400',
        inStateTuition: '$14,070',
      },
      demographics: {
        maleFemaleRatio: '46% / 54%',
        internationalStudents: '6.0',
        raceEthnicity: {
          'Asian': 28.0,
          'Hispanic': 27.0,
          'White': 32.0,
          'Black': 3.0,
          'Two or more races': 6.0,
          'Nonresident Alien': 6.0,
          'Unknown': 5.0,
        },
      },
    },
  },
  {
    name: 'Drexel University',
    location: 'Philadelphia, PA',
    type: 'Private',
    stats: {
      acceptanceRate: '83.0',
      gpa: { p25: '3.60', p75: '3.90' },
      sat: { p25: 1170, p75: 1380 },
      act: { p25: 25, p75: 31 },
      yieldRate: '8',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '11:1',
      fourYearGraduationRate: '52',
      studentCount: '14,552',
      cost: {
        tuitionAndFees: '$58,965',
        roomAndBoard: '$16,008',
        inStateTuition: '$58,965',
      },
      demographics: {
        maleFemaleRatio: '51% male / 49% female',
        internationalStudents: '12',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Howard University',
    location: 'Washington, DC',
    type: 'Private',
    stats: {
      acceptanceRate: '35.0',
      gpa: { p25: '3.25', p75: '3.75' },
      sat: { p25: 1080, p75: 1290 },
      act: { p25: 22, p75: 27 },
      yieldRate: '22',
      applicationDeadline: 'Feb 15',
      studentFacultyRatio: '12:1',
      fourYearGraduationRate: '51',
      studentCount: '8,964',
      cost: {
        tuitionAndFees: '$31,050',
        roomAndBoard: '$15,650',
        inStateTuition: '$31,050',
      },
      demographics: {
        maleFemaleRatio: '28% male / 72% female',
        internationalStudents: '5',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Marquette University',
    location: 'Milwaukee, WI',
    type: 'Private',
    stats: {
      acceptanceRate: '86.0',
      gpa: { p25: '3.40', p75: '3.90' },
      sat: { p25: 1150, p75: 1330 },
      act: { p25: 24, p75: 30 },
      yieldRate: '13',
      applicationDeadline: 'Dec 1',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '68',
      studentCount: '7,528',
      cost: {
        tuitionAndFees: '$47,690',
        roomAndBoard: '$13,656',
        inStateTuition: '$47,690',
      },
      demographics: {
        maleFemaleRatio: '44% male / 56% female',
        internationalStudents: '2',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Delaware',
    location: 'Newark, DE',
    type: 'Public',
    stats: {
      acceptanceRate: '66.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1160, p75: 1350 },
      act: { p25: 25, p75: 30 },
      yieldRate: '23',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '12:1',
      fourYearGraduationRate: '72',
      studentCount: '15,503',
      cost: {
        tuitionAndFees: '$65,204',
        roomAndBoard: '$19,800',
        inStateTuition: '$65,204',
      },
      demographics: {
        maleFemaleRatio: '49% / 51%',
        internationalStudents: '13.0',
        raceEthnicity: {
          'Asian': 25.0,
          'Hispanic': 12.0,
          'White': 35.0,
          'Black': 7.0,
          'Two or more races': 5.0,
          'Nonresident Alien': 13.0,
          'Unknown': 3.0,
        },
      },
    },
  },
  {
    name: 'Auburn University',
    location: 'Auburn, AL',
    type: 'Public',
    stats: {
      acceptanceRate: '44.0',
      gpa: { p25: '3.80', p75: '4.40' },
      sat: { p25: 1160, p75: 1320 },
      act: { p25: 25, p75: 31 },
      yieldRate: '30',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '20:1',
      fourYearGraduationRate: '51',
      studentCount: '24,931',
      cost: {
        tuitionAndFees: '$11,826',
        roomAndBoard: '$13,780',
        inStateTuition: '$11,826',
      },
      demographics: {
        maleFemaleRatio: '50% male / 50% female',
        internationalStudents: '2',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Illinois Institute of Technology',
    location: 'Chicago, IL',
    type: 'Private',
    stats: {
      acceptanceRate: '66.0',
      gpa: { p25: '3.40', p75: '3.90' },
      sat: { p25: 1200, p75: 1390 },
      act: { p25: 26, p75: 32 },
      yieldRate: '13',
      applicationDeadline: 'Aug 1',
      studentFacultyRatio: '12:1',
      fourYearGraduationRate: '47',
      studentCount: '3,094',
      cost: {
        tuitionAndFees: '$50,640',
        roomAndBoard: '$14,500',
        inStateTuition: '$50,640',
      },
      demographics: {
        maleFemaleRatio: '69% male / 31% female',
        internationalStudents: '20',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Saint Louis University',
    location: 'St. Louis, MO',
    type: 'Private',
    stats: {
      acceptanceRate: '82.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1170, p75: 1370 },
      act: { p25: 25, p75: 31 },
      yieldRate: '18',
      applicationDeadline: 'Dec 1',
      studentFacultyRatio: '9:1',
      fourYearGraduationRate: '70',
      studentCount: '7,426',
      cost: {
        tuitionAndFees: '$50,644',
        roomAndBoard: '$13,600',
        inStateTuition: '$50,644',
      },
      demographics: {
        maleFemaleRatio: '41% male / 59% female',
        internationalStudents: '4',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Texas Christian University',
    location: 'Fort Worth, TX',
    type: 'Private',
    stats: {
      acceptanceRate: '54.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1150, p75: 1340 },
      act: { p25: 26, p75: 31 },
      yieldRate: '23',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '70',
      studentCount: '10,222',
      cost: {
        tuitionAndFees: '$53,890',
        roomAndBoard: '$14,200',
        inStateTuition: '$53,890',
      },
      demographics: {
        maleFemaleRatio: '41% male / 59% female',
        internationalStudents: '3',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Brigham Young University',
    location: 'Provo, UT',
    type: 'Private',
    stats: {
      acceptanceRate: '59.0',
      gpa: { p25: '3.80', p75: '4.00' },
      sat: { p25: 1270, p75: 1450 },
      act: { p25: 27, p75: 32 },
      yieldRate: '78',
      applicationDeadline: 'Dec 15',
      studentFacultyRatio: '20:1',
      fourYearGraduationRate: '54',
      studentCount: '31,389',
      cost: {
        tuitionAndFees: '$6,304',
        roomAndBoard: '$8,048',
        inStateTuition: '$6,304',
      },
      demographics: {
        maleFemaleRatio: '44% male / 56% female',
        internationalStudents: '6',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'California State University–Long Beach',
    location: 'Long Beach, CA',
    type: 'Public',
    stats: {
      acceptanceRate: '47.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1040, p75: 1250 },
      act: { p25: 20, p75: 26 },
      yieldRate: '18',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '23:1',
      fourYearGraduationRate: '25',
      studentCount: '32,785',
      cost: {
        tuitionAndFees: '$6,884',
        roomAndBoard: '$15,918',
        inStateTuition: '$6,884',
      },
      demographics: {
        maleFemaleRatio: '43% male / 57% female',
        internationalStudents: '5',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'George Mason University',
    location: 'Fairfax, VA',
    type: 'Public',
    stats: {
      acceptanceRate: '91.0',
      gpa: { p25: '3.30', p75: '3.90' },
      sat: { p25: 1140, p75: 1320 },
      act: { p25: 24, p75: 30 },
      yieldRate: '22',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '16:1',
      fourYearGraduationRate: '52',
      studentCount: '27,104',
      cost: {
        tuitionAndFees: '$13,401',
        roomAndBoard: '$12,630',
        inStateTuition: '$13,401',
      },
      demographics: {
        maleFemaleRatio: '48% male / 52% female',
        internationalStudents: '8',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'San Diego State University',
    location: 'San Diego, CA',
    type: 'Public',
    stats: {
      acceptanceRate: '38.0',
      gpa: { p25: '3.70', p75: '4.00' },
      sat: { p25: 1110, p75: 1320 },
      act: { p25: 22, p75: 29 },
      yieldRate: '20',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '25:1',
      fourYearGraduationRate: '38',
      studentCount: '31,724',
      cost: {
        tuitionAndFees: '$8,174',
        roomAndBoard: '$18,531',
        inStateTuition: '$8,174',
      },
      demographics: {
        maleFemaleRatio: '44% male / 56% female',
        internationalStudents: '4',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Arizona',
    location: 'Tucson, AZ',
    type: 'Public',
    stats: {
      acceptanceRate: '87.0',
      gpa: { p25: '3.40', p75: '4.00' },
      sat: { p25: 1120, p75: 1370 },
      act: { p25: 21, p75: 29 },
      yieldRate: '23',
      applicationDeadline: 'May 1',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '50',
      studentCount: '36,503',
      cost: {
        tuitionAndFees: '$12,404',
        roomAndBoard: '$13,050',
        inStateTuition: '$12,404',
      },
      demographics: {
        maleFemaleRatio: '45% male / 55% female',
        internationalStudents: '6',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Missouri',
    location: 'Columbia, MO',
    type: 'Public',
    stats: {
      acceptanceRate: '77.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1120, p75: 1340 },
      act: { p25: 23, p75: 29 },
      yieldRate: '28',
      applicationDeadline: 'Dec 1',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '51',
      studentCount: '23,396',
      cost: {
        tuitionAndFees: '$11,981',
        roomAndBoard: '$10,508',
        inStateTuition: '$11,981',
      },
      demographics: {
        maleFemaleRatio: '45% male / 55% female',
        internationalStudents: '4',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of New Hampshire',
    location: 'Durham, NH',
    type: 'Public',
    stats: {
      acceptanceRate: '87.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1080, p75: 1280 },
      act: { p25: 23, p75: 29 },
      yieldRate: '18',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '66',
      studentCount: '11,747',
      cost: {
        tuitionAndFees: '$18,962',
        roomAndBoard: '$13,636',
        inStateTuition: '$18,962',
      },
      demographics: {
        maleFemaleRatio: '45% male / 55% female',
        internationalStudents: '2',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Oregon',
    location: 'Eugene, OR',
    type: 'Public',
    stats: {
      acceptanceRate: '86.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1100, p75: 1310 },
      act: { p25: 22, p75: 29 },
      yieldRate: '20',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '55',
      studentCount: '18,045',
      cost: {
        tuitionAndFees: '$14,421',
        roomAndBoard: '$13,869',
        inStateTuition: '$14,421',
      },
      demographics: {
        maleFemaleRatio: '45% male / 55% female',
        internationalStudents: '6',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of San Diego',
    location: 'San Diego, CA',
    type: 'Private',
    stats: {
      acceptanceRate: '59.0',
      gpa: { p25: '3.80', p75: '4.00' },
      sat: { p25: 1220, p75: 1370 },
      act: { p25: 27, p75: 32 },
      yieldRate: '18',
      applicationDeadline: 'Dec 15',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '70',
      studentCount: '5,529',
      cost: {
        tuitionAndFees: '$54,554',
        roomAndBoard: '$16,000',
        inStateTuition: '$54,554',
      },
      demographics: {
        maleFemaleRatio: '43% male / 57% female',
        internationalStudents: '4',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of San Francisco',
    location: 'San Francisco, CA',
    type: 'Private',
    stats: {
      acceptanceRate: '71.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1130, p75: 1330 },
      act: { p25: 24, p75: 30 },
      yieldRate: '13',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '66',
      studentCount: '6,577',
      cost: {
        tuitionAndFees: '$54,980',
        roomAndBoard: '$16,120',
        inStateTuition: '$54,980',
      },
      demographics: {
        maleFemaleRatio: '38% male / 62% female',
        internationalStudents: '14',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Tennessee–Knoxville',
    location: 'Knoxville, TN',
    type: 'Public',
    stats: {
      acceptanceRate: '68.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1170, p75: 1330 },
      act: { p25: 25, p75: 31 },
      yieldRate: '30',
      applicationDeadline: 'Dec 15',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '54',
      studentCount: '25,067',
      cost: {
        tuitionAndFees: '$13,244',
        roomAndBoard: '$12,150',
        inStateTuition: '$13,244',
      },
      demographics: {
        maleFemaleRatio: '45% male / 55% female',
        internationalStudents: '3',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'The University of Texas–Dallas',
    location: 'Richardson, TX',
    type: 'Public',
    stats: {
      acceptanceRate: '65.4',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1220, p75: 1440 },
      act: { p25: 26, p75: 33 },
      yieldRate: '34',
      applicationDeadline: 'May 1',
      studentFacultyRatio: '24:1',
      fourYearGraduationRate: '56',
      studentCount: '21,445',
      cost: {
        tuitionAndFees: '$14,564',
        roomAndBoard: '$12,142',
        inStateTuition: '$14,564',
      },
      demographics: {
        maleFemaleRatio: '57% male / 43% female',
        internationalStudents: '17',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Arizona State University',
    location: 'Tempe, AZ',
    type: 'Public',
    stats: {
      acceptanceRate: '90.0',
      gpa: { p25: '3.40', p75: '4.00' },
      sat: { p25: 1120, p75: 1370 },
      act: { p25: 22, p75: 29 },
      yieldRate: '33',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '18:1',
      fourYearGraduationRate: '52',
      studentCount: '65,492',
      cost: {
        tuitionAndFees: '$11,618',
        roomAndBoard: '$13,510',
        inStateTuition: '$11,618',
      },
      demographics: {
        maleFemaleRatio: '50% male / 50% female',
        internationalStudents: '8',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Chapman University',
    location: 'Orange, CA',
    type: 'Private',
    stats: {
      acceptanceRate: '60.0',
      gpa: { p25: '3.70', p75: '4.00' },
      sat: { p25: 1200, p75: 1370 },
      act: { p25: 26, p75: 31 },
      yieldRate: '20',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '70',
      studentCount: '7,656',
      cost: {
        tuitionAndFees: '$60,672',
        roomAndBoard: '$16,000',
        inStateTuition: '$60,672',
      },
      demographics: {
        maleFemaleRatio: '40% male / 60% female',
        internationalStudents: '5',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Creighton University',
    location: 'Omaha, NE',
    type: 'Private',
    stats: {
      acceptanceRate: '78.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1150, p75: 1340 },
      act: { p25: 24, p75: 30 },
      yieldRate: '20',
      applicationDeadline: 'Nov 1',
      studentFacultyRatio: '11:1',
      fourYearGraduationRate: '70',
      studentCount: '4,472',
      cost: {
        tuitionAndFees: '$45,590',
        roomAndBoard: '$13,500',
        inStateTuition: '$45,590',
      },
      demographics: {
        maleFemaleRatio: '40% male / 60% female',
        internationalStudents: '2',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'CUNY—City College',
    location: 'New York, NY',
    type: 'Public',
    stats: {
      acceptanceRate: '64.0',
      gpa: { p25: '3.20', p75: '3.80' },
      sat: { p25: 1090, p75: 1290 },
      act: { p25: 22, p75: 28 },
      yieldRate: '20',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '15:1',
      fourYearGraduationRate: '34',
      studentCount: '13,030',
      cost: {
        tuitionAndFees: '$7,340',
        roomAndBoard: '$12,000',
        inStateTuition: '$7,340',
      },
      demographics: {
        maleFemaleRatio: '51% male / 49% female',
        internationalStudents: '7',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Elon University',
    location: 'Elon, NC',
    type: 'Private',
    stats: {
      acceptanceRate: '78.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1170, p75: 1330 },
      act: { p25: 25, p75: 30 },
      yieldRate: '20',
      applicationDeadline: 'Jan 10',
      studentFacultyRatio: '12:1',
      fourYearGraduationRate: '78',
      studentCount: '6,291',
      cost: {
        tuitionAndFees: '$42,241',
        roomAndBoard: '$14,000',
        inStateTuition: '$42,241',
      },
      demographics: {
        maleFemaleRatio: '40% male / 60% female',
        internationalStudents: '2',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Iowa State University of Science and Technology',
    location: 'Ames, IA',
    type: 'Public',
    stats: {
      acceptanceRate: '90.0',
      gpa: { p25: '3.40', p75: '4.00' },
      sat: { p25: 1080, p75: 1330 },
      act: { p25: 22, p75: 29 },
      yieldRate: '33',
      applicationDeadline: 'Jul 1',
      studentFacultyRatio: '18:1',
      fourYearGraduationRate: '48',
      studentCount: '25,808',
      cost: {
        tuitionAndFees: '$9,316',
        roomAndBoard: '$9,358',
        inStateTuition: '$9,316',
      },
      demographics: {
        maleFemaleRatio: '56% male / 44% female',
        internationalStudents: '7',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University at Albany (SUNY)',
    location: 'Albany, NY',
    type: 'Public',
    stats: {
      acceptanceRate: '68.0',
      gpa: { p25: '3.10', p75: '3.60' },
      sat: { p25: 1080, p75: 1260 },
      act: { p25: 22, p75: 28 },
      yieldRate: '18',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '18:1',
      fourYearGraduationRate: '54',
      studentCount: '13,182',
      cost: {
        tuitionAndFees: '$10,408',
        roomAndBoard: '$15,936',
        inStateTuition: '$10,408',
      },
      demographics: {
        maleFemaleRatio: '47% male / 53% female',
        internationalStudents: '6',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Central Florida',
    location: 'Orlando, FL',
    type: 'Public',
    stats: {
      acceptanceRate: '36.0',
      gpa: { p25: '3.80', p75: '4.30' },
      sat: { p25: 1200, p75: 1360 },
      act: { p25: 25, p75: 30 },
      yieldRate: '35',
      applicationDeadline: 'May 1',
      studentFacultyRatio: '30:1',
      fourYearGraduationRate: '50',
      studentCount: '58,749',
      cost: {
        tuitionAndFees: '$6,368',
        roomAndBoard: '$11,498',
        inStateTuition: '$6,368',
      },
      demographics: {
        maleFemaleRatio: '44% male / 56% female',
        internationalStudents: '4',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Denver',
    location: 'Denver, CO',
    type: 'Private',
    stats: {
      acceptanceRate: '64.0',
      gpa: { p25: '3.70', p75: '4.00' },
      sat: { p25: 1180, p75: 1370 },
      act: { p25: 26, p75: 31 },
      yieldRate: '10',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '11:1',
      fourYearGraduationRate: '67',
      studentCount: '5,867',
      cost: {
        tuitionAndFees: '$56,439',
        roomAndBoard: '$15,060',
        inStateTuition: '$56,439',
      },
      demographics: {
        maleFemaleRatio: '43% male / 57% female',
        internationalStudents: '7',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of South Carolina',
    location: 'Columbia, SC',
    type: 'Public',
    stats: {
      acceptanceRate: '62.0',
      gpa: { p25: '3.80', p75: '4.30' },
      sat: { p25: 1140, p75: 1340 },
      act: { p25: 25, p75: 31 },
      yieldRate: '28',
      applicationDeadline: 'Dec 1',
      studentFacultyRatio: '18:1',
      fourYearGraduationRate: '62',
      studentCount: '26,864',
      cost: {
        tuitionAndFees: '$12,688',
        roomAndBoard: '$11,996',
        inStateTuition: '$12,688',
      },
      demographics: {
        maleFemaleRatio: '44% male / 56% female',
        internationalStudents: '3',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Vermont',
    location: 'Burlington, VT',
    type: 'Public',
    stats: {
      acceptanceRate: '64.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1180, p75: 1360 },
      act: { p25: 26, p75: 31 },
      yieldRate: '18',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '16:1',
      fourYearGraduationRate: '67',
      studentCount: '10,700',
      cost: {
        tuitionAndFees: '$20,540',
        roomAndBoard: '$13,354',
        inStateTuition: '$20,540',
      },
      demographics: {
        maleFemaleRatio: '41% male / 59% female',
        internationalStudents: '2',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Clark University',
    location: 'Worcester, MA',
    type: 'Private',
    stats: {
      acceptanceRate: '48.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1200, p75: 1380 },
      act: { p25: 27, p75: 32 },
      yieldRate: '15',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '10:1',
      fourYearGraduationRate: '70',
      studentCount: '2,350',
      cost: {
        tuitionAndFees: '$54,370',
        roomAndBoard: '$14,000',
        inStateTuition: '$54,370',
      },
      demographics: {
        maleFemaleRatio: '40% male / 60% female',
        internationalStudents: '13',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Fairfield University',
    location: 'Fairfield, CT',
    type: 'Private',
    stats: {
      acceptanceRate: '56.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1200, p75: 1350 },
      act: { p25: 27, p75: 31 },
      yieldRate: '15',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '12:1',
      fourYearGraduationRate: '82',
      studentCount: '4,354',
      cost: {
        tuitionAndFees: '$54,455',
        roomAndBoard: '$16,000',
        inStateTuition: '$54,455',
      },
      demographics: {
        maleFemaleRatio: '41% male / 59% female',
        internationalStudents: '2',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Loyola University Chicago',
    location: 'Chicago, IL',
    type: 'Private',
    stats: {
      acceptanceRate: '77.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1160, p75: 1340 },
      act: { p25: 25, p75: 31 },
      yieldRate: '13',
      applicationDeadline: 'Dec 1',
      studentFacultyRatio: '14:1',
      fourYearGraduationRate: '68',
      studentCount: '11,703',
      cost: {
        tuitionAndFees: '$49,498',
        roomAndBoard: '$15,000',
        inStateTuition: '$49,498',
      },
      demographics: {
        maleFemaleRatio: '33% male / 67% female',
        internationalStudents: '5',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'The University of Oklahoma',
    location: 'Norman, OK',
    type: 'Public',
    stats: {
      acceptanceRate: '83.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1130, p75: 1340 },
      act: { p25: 23, p75: 29 },
      yieldRate: '33',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '41',
      studentCount: '21,383',
      cost: {
        tuitionAndFees: '$12,012',
        roomAndBoard: '$11,700',
        inStateTuition: '$12,012',
      },
      demographics: {
        maleFemaleRatio: '48% male / 52% female',
        internationalStudents: '4',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'California State University–Fullerton',
    location: 'Fullerton, CA',
    type: 'Public',
    stats: {
      acceptanceRate: '67.0',
      gpa: { p25: '3.40', p75: '3.90' },
      sat: { p25: 1030, p75: 1220 },
      act: { p25: 19, p75: 25 },
      yieldRate: '18',
      applicationDeadline: 'Nov 30',
      studentFacultyRatio: '25:1',
      fourYearGraduationRate: '25',
      studentCount: '35,169',
      cost: {
        tuitionAndFees: '$6,976',
        roomAndBoard: '$16,000',
        inStateTuition: '$6,976',
      },
      demographics: {
        maleFemaleRatio: '44% male / 56% female',
        internationalStudents: '3',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Miami University—Oxford',
    location: 'Oxford, OH',
    type: 'Public',
    stats: {
      acceptanceRate: '88.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1200, p75: 1370 },
      act: { p25: 26, p75: 31 },
      yieldRate: '18',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '14:1',
      fourYearGraduationRate: '74',
      studentCount: '16,522',
      cost: {
        tuitionAndFees: '$17,544',
        roomAndBoard: '$15,246',
        inStateTuition: '$17,544',
      },
      demographics: {
        maleFemaleRatio: '45% male / 55% female',
        internationalStudents: '3',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Thomas Jefferson University',
    location: 'Philadelphia, PA',
    type: 'Private',
    stats: {
      acceptanceRate: '78.0',
      gpa: { p25: '3.20', p75: '3.80' },
      sat: { p25: 1060, p75: 1260 },
      act: { p25: 21, p75: 28 },
      yieldRate: '15',
      applicationDeadline: 'Mar 1',
      studentFacultyRatio: '12:1',
      fourYearGraduationRate: '56',
      studentCount: '3,783',
      cost: {
        tuitionAndFees: '$43,100',
        roomAndBoard: '$13,500',
        inStateTuition: '$43,100',
      },
      demographics: {
        maleFemaleRatio: '35% male / 65% female',
        internationalStudents: '3',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Alabama at Birmingham',
    location: 'Birmingham, AL',
    type: 'Public',
    stats: {
      acceptanceRate: '89.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1080, p75: 1320 },
      act: { p25: 21, p75: 29 },
      yieldRate: '25',
      applicationDeadline: 'Jun 1',
      studentFacultyRatio: '19:1',
      fourYearGraduationRate: '32',
      studentCount: '13,878',
      cost: {
        tuitionAndFees: '$8,568',
        roomAndBoard: '$13,350',
        inStateTuition: '$8,568',
      },
      demographics: {
        maleFemaleRatio: '40% male / 60% female',
        internationalStudents: '4',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Dayton',
    location: 'Dayton, OH',
    type: 'Private',
    stats: {
      acceptanceRate: '81.0',
      gpa: { p25: '3.60', p75: '4.00' },
      sat: { p25: 1130, p75: 1320 },
      act: { p25: 24, p75: 30 },
      yieldRate: '20',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '15:1',
      fourYearGraduationRate: '68',
      studentCount: '8,225',
      cost: {
        tuitionAndFees: '$46,170',
        roomAndBoard: '$15,000',
        inStateTuition: '$46,170',
      },
      demographics: {
        maleFemaleRatio: '53% male / 47% female',
        internationalStudents: '2',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of the Pacific',
    location: 'Stockton, CA',
    type: 'Private',
    stats: {
      acceptanceRate: '79.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1110, p75: 1340 },
      act: { p25: 23, p75: 30 },
      yieldRate: '13',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '60',
      studentCount: '3,294',
      cost: {
        tuitionAndFees: '$54,506',
        roomAndBoard: '$16,000',
        inStateTuition: '$54,506',
      },
      demographics: {
        maleFemaleRatio: '44% male / 56% female',
        internationalStudents: '6',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Utah',
    location: 'Salt Lake City, UT',
    type: 'Public',
    stats: {
      acceptanceRate: '95.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1130, p75: 1350 },
      act: { p25: 22, p75: 29 },
      yieldRate: '30',
      applicationDeadline: 'Apr 1',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '38',
      studentCount: '26,355',
      cost: {
        tuitionAndFees: '$9,816',
        roomAndBoard: '$12,260',
        inStateTuition: '$9,816',
      },
      demographics: {
        maleFemaleRatio: '50% male / 50% female',
        internationalStudents: '5',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Virginia Commonwealth University',
    location: 'Richmond, VA',
    type: 'Public',
    stats: {
      acceptanceRate: '91.0',
      gpa: { p25: '3.30', p75: '3.90' },
      sat: { p25: 1070, p75: 1270 },
      act: { p25: 21, p75: 28 },
      yieldRate: '20',
      applicationDeadline: 'Jan 15',
      studentFacultyRatio: '18:1',
      fourYearGraduationRate: '38',
      studentCount: '21,394',
      cost: {
        tuitionAndFees: '$15,642',
        roomAndBoard: '$12,324',
        inStateTuition: '$15,642',
      },
      demographics: {
        maleFemaleRatio: '38% male / 62% female',
        internationalStudents: '4',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'Oregon State University',
    location: 'Corvallis, OR',
    type: 'Public',
    stats: {
      acceptanceRate: '83.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1080, p75: 1320 },
      act: { p25: 21, p75: 28 },
      yieldRate: '25',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '18:1',
      fourYearGraduationRate: '38',
      studentCount: '25,699',
      cost: {
        tuitionAndFees: '$12,664',
        roomAndBoard: '$13,860',
        inStateTuition: '$12,664',
      },
      demographics: {
        maleFemaleRatio: '48% male / 52% female',
        internationalStudents: '7',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'SUNY College of Environmental Science and Forestry',
    location: 'Syracuse, NY',
    type: 'Public',
    stats: {
      acceptanceRate: '65.0',
      gpa: { p25: '3.40', p75: '3.90' },
      sat: { p25: 1120, p75: 1300 },
      act: { p25: 23, p75: 29 },
      yieldRate: '20',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '13:1',
      fourYearGraduationRate: '62',
      studentCount: '1,776',
      cost: {
        tuitionAndFees: '$8,720',
        roomAndBoard: '$14,000',
        inStateTuition: '$8,720',
      },
      demographics: {
        maleFemaleRatio: '47% male / 53% female',
        internationalStudents: '2',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Houston',
    location: 'Houston, TX',
    type: 'Public',
    stats: {
      acceptanceRate: '66.0',
      gpa: { p25: '3.40', p75: '3.90' },
      sat: { p25: 1120, p75: 1310 },
      act: { p25: 22, p75: 28 },
      yieldRate: '30',
      applicationDeadline: 'Apr 1',
      studentFacultyRatio: '22:1',
      fourYearGraduationRate: '32',
      studentCount: '38,597',
      cost: {
        tuitionAndFees: '$11,276',
        roomAndBoard: '$10,810',
        inStateTuition: '$11,276',
      },
      demographics: {
        maleFemaleRatio: '46% male / 54% female',
        internationalStudents: '8',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
  {
    name: 'University of Maryland, Baltimore County',
    location: 'Baltimore, MD',
    type: 'Public',
    stats: {
      acceptanceRate: '81.0',
      gpa: { p25: '3.50', p75: '4.00' },
      sat: { p25: 1170, p75: 1360 },
      act: { p25: 24, p75: 30 },
      yieldRate: '20',
      applicationDeadline: 'Feb 1',
      studentFacultyRatio: '17:1',
      fourYearGraduationRate: '38',
      studentCount: '10,932',
      cost: {
        tuitionAndFees: '$12,606',
        roomAndBoard: '$13,324',
        inStateTuition: '$12,606',
      },
      demographics: {
        maleFemaleRatio: '54% male / 46% female',
        internationalStudents: '6',
        racialEthnicDiversity: 'N/A',
      },
    },
  },
];

// --- Data Standardization ---
// Ensure all colleges have satRange and actRange for consistency
collegeData.forEach(college => {
  if (college.stats) {
    // Standardize SAT Range
    if (college.stats.sat && college.stats.sat.p25 && college.stats.sat.p75 && !college.stats.satRange) {
      college.stats.satRange = `${college.stats.sat.p25}-${college.stats.sat.p75}`;
    }
    // Standardize ACT Range
    if (college.stats.act && college.stats.act.p25 && college.stats.act.p75 && !college.stats.actRange) {
      college.stats.actRange = `${college.stats.act.p25}-${college.stats.act.p75}`;
    }
  }
});
